import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {effect, inject, Injectable, PLATFORM_ID, signal} from '@angular/core';

export type AppTheme = 'light' | 'dark' | 'ninon';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private static readonly STORAGE_KEY = 'crm-agentique.theme';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT, {optional: true});

  private readonly _theme = signal<AppTheme>('light');
  readonly theme = this._theme.asReadonly();

  constructor() {
    effect(() => {
      const theme = this._theme();

      this.document?.documentElement.setAttribute('data-theme', theme);
      this.document?.documentElement.style.setProperty('color-scheme', theme === 'dark' ? 'dark' : 'light');

      if (isPlatformBrowser(this.platformId)) {
        globalThis.localStorage.setItem(ThemeService.STORAGE_KEY, theme);
      }
    });
  }

  initialize(): void {
    this._theme.set(this.resolveInitialTheme());
  }

  setTheme(theme: AppTheme): void {
    this._theme.set(theme);
  }

  coerceTheme(theme: string | null | undefined): AppTheme | null {
    return theme === 'light' || theme === 'dark' || theme === 'ninon' ? theme : null;
  }

  private resolveInitialTheme(): AppTheme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }

    const storedTheme = this.coerceTheme(globalThis.localStorage.getItem(ThemeService.STORAGE_KEY));
    if (storedTheme) {
      return storedTheme;
    }

    return 'light';
  }
}
